import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import IconButton from '@mui/material/IconButton';
import WarningIcon from '@mui/icons-material/Warning';
import Icon from '@mui/icons-material/ChevronRight';

import completeIcon from '../../assets/complete_tick_icon.svg';
import scansIcon from '../../assets/scans_icon.svg';
import preopIcon from '../../assets/preop_icon.svg';
import blockIcon from '../../assets/block_icon.svg';

import { caseConstants } from '../../constants';

import './day-info.scss';

const DayInfo = ({ selectedDay, cases, goToCase, goToFiles }) => {
  const { caseActions, numberOfSteps, statuses, fileTypes } = caseConstants;

  const renderIcon = (item) => {
    const access = item.access;
    const complete = item.step > numberOfSteps;
    const rejected = item.status === statuses.rejected;

    if (rejected) {
      return <WarningIcon color={access ? 'primary' : 'secondary'} />;
    }
    if (complete) {
      return <img src={completeIcon} alt="complete-icon" width={15} />;
    }
    return <div className={`indicator ${access ? 'active' : ''}`} />;
  };

  const renderFileIcons = (files, caseId) => {
    const scans = files.filter(
      (file) => file.type === fileTypes.scan || file.type === fileTypes.xRay
    );
    const preop = files.filter((file) => file.type === fileTypes.preop);
    const manufacturing = files.filter((file) => file.type === fileTypes.manufacturing);

    return (
      <div className="case-files">
        {scans && !!scans.length && (
          <img src={scansIcon} alt="scans-icon" onClick={() => goToFiles(caseId)} />
        )}
        {preop && !!preop.length && (
          <img src={preopIcon} alt="scans-icon" onClick={() => goToFiles(caseId)} />
        )}
        {manufacturing && !!manufacturing.length && (
          <img src={blockIcon} alt="scans-icon" onClick={() => goToFiles(caseId)} />
        )}
      </div>
    );
  };

  return (
    <div className="day-info-container">
      <h4>{format(selectedDay, 'PPP')}</h4>
      {!cases.length ? (
        <div className="secondary m-t-lg">No assigned cases this day</div>
      ) : (
        <div className="cases-list">
          {cases.map((item) => (
            <div key={item.id} className="case-item">
              <div className="d-flex space-between">
                <div className="d-flex">
                  <div className="case-indicator">{renderIcon(item)}</div>
                  <div className="font-size-bg">{item.formattedId || item.id}</div>
                </div>
                <div>
                  <IconButton
                    aria-label="go-to-case"
                    color="primary"
                    onClick={() => goToCase(item.id)}
                    size="small"
                  >
                    <Icon />
                  </IconButton>
                </div>
              </div>
              <div className="d-flex space-between m-t-md">
                <div>
                  <div className="secondary uppercase font-size-sm">Step</div>
                  <div>
                    <span className="primary">
                      {item.step > numberOfSteps ? numberOfSteps : item.step}
                    </span>
                    /{numberOfSteps}
                  </div>
                </div>
                {item.procedureName && (
                  <div>
                    <div className="secondary uppercase font-size-sm">Procedure</div>
                    <div>{item.procedureName}</div>
                  </div>
                )}
              </div>
              {item.access && (
                <div className="m-t-md">
                  <div className="secondary uppercase font-size-sm">Requiring action</div>
                  <div className="m-r-md flex-1 primary pointer" onClick={() => goToCase(item.id)}>
                    {caseActions[item.step] || ''}
                  </div>
                </div>
              )}
              <div className="m-t-md">
                <div className="secondary uppercase font-size-sm">Hospital</div>
                <div>{item.hospital}</div>
              </div>
              {item.files && !!item.files.length && (
                <div className="m-t-md">
                  <div className="secondary uppercase font-size-sm">Associated files</div>
                  {renderFileIcons(item.files, item.id)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

DayInfo.propTypes = {
  selectedDay: PropTypes.instanceOf(Date),
  cases: PropTypes.array,
  goToCase: PropTypes.func,
  goToFiles: PropTypes.func,
};

export default memo(DayInfo);
